import { CommonModule } from "@angular/common";
import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { filter } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { WorkHourPopupComponent } from "../../../components/standalones/work-hour-popup/work-hour-popup.component";
import { RestaurantWordsService } from "../../../services/restaurant-words/restaurant-words.service";
import { CompanyApiService } from "../../../services/generated/waba-api/services/company-api.service";
import { getCornerDrawerDialogPosition } from "../../../shared/utils/rtl-layout.util";
import { SILENT_HTTP_CONTEXT } from "../../../shared/utils/silent-http.util";
import {
  RestaurantFacts,
  RestaurantWarning,
  restaurantWarnings,
  warningsForUrl,
} from "../restaurant-health";
import { RestaurantHealthService } from "../restaurant-health.service";

/** Facts are read again at most once a minute while the merchant moves between pages. */
const REFRESH_MS = 60_000;

/**
 * Restaurants only. On the dashboard: every warning. On a related page
 * (dishes, orders, sales points): the warnings about that page. Each warning has one button that fixes it.
 */
@Component({
  selector: "app-restaurant-health-panel",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: "./restaurant-health-panel.component.html",
  styleUrls: ["./restaurant-health-panel.component.scss"],
})
export class RestaurantHealthPanelComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly words = inject(RestaurantWordsService);
  private readonly health = inject(RestaurantHealthService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  isRestaurant = false;
  isDashboard = false;
  warnings: RestaurantWarning[] = [];
  fixing: string | null = null;

  private facts: RestaurantFacts | null = null;
  private loadedAt = 0;
  private lastUrl = "";

  ngOnInit(): void {
    this.words.restaurant$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((restaurant) => {
      this.isRestaurant = restaurant;
      this.refresh();
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const closedPanel = this.lastUrl.includes("panel=") && !this.router.url.includes("panel=");
        this.lastUrl = this.router.url;
        if (closedPanel) {
          this.loadedAt = 0;
        }
        this.refresh();
      });
  }

  fix(warning: RestaurantWarning): void {
    const fix = warning.fix;
    if (fix.action === "NAVIGATE") {
      this.router.navigateByUrl(fix.path);
    } else if (fix.action === "REOPEN") {
      this.fixing = warning.id;
      this.health.reopen().subscribe({
        next: () => this.reload(),
        error: () => (this.fixing = null),
      });
    } else {
      this.openHours();
    }
  }

  private openHours(): void {
    this.companyApi
      .companyNumbersList({ "x-api-canal": "web", "x-api-requestId": uuidv4() }, SILENT_HTTP_CONTEXT)
      .subscribe((response) => {
        const phone = (response.body || []).find((p) => !!p.reference);
        if (!phone?.reference) {
          return;
        }
        this.dialog
          .open(WorkHourPopupComponent, {
            width: "min(560px, 100vw)",
            maxWidth: "100vw",
            height: "100vh",
            maxHeight: "100vh",
            position: getCornerDrawerDialogPosition(),
            panelClass: "payment-drawer",
            hasBackdrop: true,
            autoFocus: false,
            enterAnimationDuration: "0ms",
            exitAnimationDuration: "0ms",
            data: { refPhoneCompany: phone.reference },
          })
          .afterClosed()
          .subscribe(() => this.reload());
      });
  }

  private refresh(): void {
    if (!this.isRestaurant) {
      this.warnings = [];
      return;
    }
    if (!this.facts || Date.now() - this.loadedAt > REFRESH_MS) {
      this.reload();
    } else {
      this.show();
    }
  }

  private reload(): void {
    this.loadedAt = Date.now();
    this.health.load().subscribe((facts) => {
      this.facts = facts;
      this.fixing = null;
      this.show();
    });
  }

  private show(): void {
    if (!this.facts) {
      return;
    }
    const url = this.router.url;
    this.isDashboard = url.split(/[?#]/)[0] === "/dashboard";
    this.warnings = warningsForUrl(restaurantWarnings(this.facts), url);
  }
}
